import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { NamedLink, Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { withViewport } from '../../util/uiHelpers';
import { useConfiguration } from '../../context/configurationContext';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import Icons from '../../components/Icons/Icons';

import css from './PurchaseProtectionPage.module.css';

import facebookImage from '../../assets/tradmarket-facebook-sharing-1200x630.jpg';
import twitterImage from '../../assets/tradmarket-twitter-sharing-600x315.jpg';
import shieldImage from '../../assets/purchase-protection/trad-protect-shield.svg';
import tradProtectLogo from '../../assets/purchase-protection/tradprotect-logo.svg';
import heroImage from '../../assets/purchase-protection/purchase-protection-hero.jpg';
import tradShipLogo from '../../assets/purchase-protection/tradship-logo.svg';
import tradShipBg from '../../assets/purchase-protection/tradship-banner-bg.jpg';

const buyerCards = [
  { icon: 'calendarCheck', id: 'buyer1' },
  { icon: 'photoOff', id: 'buyer2' },
  { icon: 'rotate2', id: 'buyer3' },
  { icon: 'shieldCheck', id: 'buyer4' },
];

const sellerCards = [
  { icon: 'userCheck', id: 'seller1' },
  { icon: 'cashBanknoteMove', id: 'seller2' },
  { icon: 'scale', id: 'seller3' },
  { icon: 'messages', id: 'seller4' },
];

const steps = [
  { number: '1', id: 'step1' },
  { number: '2', id: 'step2' },
  { number: '3', id: 'step3' },
];

const coveredItems = ['covered1', 'covered2', 'covered3'];

const notCoveredItems = ['notCovered1', 'notCovered2', 'notCovered3', 'notCovered4'];

export const PurchaseProtectionPageComponent = props => {
  const { intl, scrollingDisabled } = props;

  const config = useConfiguration();
  const marketplaceName = config.marketplaceName;

  const schemaTitle = intl.formatMessage(
    { id: 'PurchaseProtectionPage.schemaTitle' },
    { marketplaceName }
  );
  const schemaDescription = intl.formatMessage({
    id: 'PurchaseProtectionPage.schemaDescription',
  });
  const schemaImage = `${config.marketplaceRootURL}${facebookImage}`;

  return (
    <Page
      className={css.ppRoot}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={schemaTitle}
      facebookImages={[{ url: facebookImage, width: 1200, height: 630 }]}
      twitterImages={[
        {
          url: twitterImage,
          width: 600,
          height: 314,
        },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
        image: [schemaImage],
      }}
    >
      <LayoutSingleColumn
        className={css.ppPageRoot}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        {/* Section 1: Hero */}
        <div className={css.ppHero}>
          <div className={css.ppHeroImageContainer}>
            <img className={css.ppHeroImage} src={heroImage} alt="" />
          </div>
          <div className={css.ppHeroOverlay}>
            <div className={css.ppHeroContent}>
              <img src={tradProtectLogo} alt={intl.formatMessage({ id: 'PurchaseProtectionPage.tradProtectAlt' })} className={css.ppHeroLogo} />
              <h1 className={css.ppHeroHeading}>
                <FormattedMessage id="PurchaseProtectionPage.heroHeading" />
              </h1>
              <p className={css.ppHeroSubheading}>
                <FormattedMessage
                  id="PurchaseProtectionPage.heroSubheading"
                  values={{
                    hashtag: chunks => <span className={css.ppHashtag}>{chunks}</span>,
                    br: <br />,
                  }}
                />
              </p>
            </div>
          </div>
        </div>

        {/* Section 2: Intro */}
        <div className={css.ppIntroOuter}>
          <div className={css.ppIntroInner}>
            <p className={css.ppIntroText}>
              <FormattedMessage
                id="PurchaseProtectionPage.intro"
                values={{
                  buying: chunks => (
                    <a href="#buyer-protection" className={css.ppIntroAnchor}>
                      {chunks}
                    </a>
                  ),
                  selling: chunks => (
                    <a href="#seller-protection" className={css.ppIntroAnchor}>
                      {chunks}
                    </a>
                  ),
                }}
              />
            </p>
          </div>
        </div>

        {/* Section 3: For Buyers */}
        <div id="buyer-protection" className={css.ppSectionWhiteOuter}>
          <div className={css.ppSectionInnerNoTop}>
            <span className={css.ppEyebrow}>
              <FormattedMessage id="PurchaseProtectionPage.eyebrowBuyers" />
            </span>
            <h2 className={css.ppSectionHeading}>
              <FormattedMessage id="PurchaseProtectionPage.buyersHeading" />
            </h2>
            <div className={css.ppCardGrid}>
              {buyerCards.map((card, i) => (
                <div key={i} className={css.ppCard}>
                  <div className={css.ppCardIcon}>
                    <Icons name={card.icon} className={css.ppIcon} />
                  </div>
                  <h3 className={css.ppCardHeading}><FormattedMessage id={`PurchaseProtectionPage.${card.id}.heading`} /></h3>
                  <p className={css.ppCardBody}><FormattedMessage id={`PurchaseProtectionPage.${card.id}.body`} /></p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Section 4: How to Report an Issue */}
        <div className={css.ppSectionGrayOuter}>
          <div className={css.ppSectionInner}>
            <h2 className={css.ppSectionHeading}>
              <FormattedMessage id="PurchaseProtectionPage.reportHeading" />
            </h2>
            <p className={css.ppStepsIntro}>
              <FormattedMessage id="PurchaseProtectionPage.reportIntro" />
            </p>
            <div className={css.ppStepsGrid}>
              {steps.map((step, i) => (
                <div key={i} className={css.ppStep}>
                  <span className={css.ppStepNumber}>{step.number}</span>
                  <h3 className={css.ppStepHeading}><FormattedMessage id={`PurchaseProtectionPage.${step.id}.heading`} /></h3>
                  <p className={css.ppStepBody}><FormattedMessage id={`PurchaseProtectionPage.${step.id}.body`} /></p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Section 5: What's Covered / Not Covered */}
        <div className={css.ppSectionWhiteOuter}>
          <div className={css.ppSectionInner}>
            <h2 className={css.ppSectionHeading}>
              <FormattedMessage id="PurchaseProtectionPage.coverageHeading" />
            </h2>
            <div className={css.ppCoverageGrid}>
              <div className={css.ppCoverageColumn}>
                <h3 className={css.ppCoverageHeading}>
              <FormattedMessage id="PurchaseProtectionPage.coveredHeading" />
            </h3>
                <ul className={css.ppCoveredList}>
                  {coveredItems.map((item, i) => (
                    <li key={i} className={css.ppCoveredItem}>
                      <Icons name="check" className={css.ppCheckIcon} />
                      <span>
                        <FormattedMessage id={`PurchaseProtectionPage.${item}`} />
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={css.ppCoverageColumn}>
                <h3 className={css.ppCoverageHeading}>
              <FormattedMessage id="PurchaseProtectionPage.notCoveredHeading" />
            </h3>
                <ul className={css.ppNotCoveredList}>
                  {notCoveredItems.map((item, i) => (
                    <li key={i} className={css.ppNotCoveredItem}>
                      <Icons name="xMark" className={css.ppXIcon} />
                      <span>
                        <FormattedMessage id={`PurchaseProtectionPage.${item}`} />
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Section 6: For Sellers */}
        <div id="seller-protection" className={css.ppSectionGrayOuter}>
          <div className={css.ppSectionInner}>
            <span className={css.ppEyebrow}>
              <FormattedMessage id="PurchaseProtectionPage.eyebrowSellers" />
            </span>
            <h2 className={css.ppSectionHeading}>
              <FormattedMessage id="PurchaseProtectionPage.sellersHeading" />
            </h2>
            <div className={css.ppCardGrid}>
              {sellerCards.map((card, i) => (
                <div key={i} className={css.ppCard}>
                  <div className={css.ppCardIcon}>
                    <Icons name={card.icon} className={css.ppIcon} />
                  </div>
                  <h3 className={css.ppCardHeading}><FormattedMessage id={`PurchaseProtectionPage.${card.id}.heading`} /></h3>
                  <p className={css.ppCardBody}><FormattedMessage id={`PurchaseProtectionPage.${card.id}.body`} /></p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* TradShip Banner */}
        <div className={css.ppTradShipBanner}>
          <img className={css.ppTradShipBg} src={tradShipBg} alt="" />
          <div className={css.ppTradShipOverlay}>
            <img src={tradShipLogo} alt={intl.formatMessage({ id: 'PurchaseProtectionPage.tradShipAlt' })} className={css.ppTradShipLogo} />
            <p className={css.ppTradShipCopy}>
              <FormattedMessage id="PurchaseProtectionPage.tradShipCopy" />
            </p>
          </div>
        </div>

        {/* Section 7: Closing CTAs */}
        <div className={css.ppClosingOuter}>
          <div className={css.ppClosingInner}>
            <h2 className={css.ppClosingHeading}>
              <FormattedMessage id="PurchaseProtectionPage.closingHeading" />
            </h2>
            <div className={css.ppClosingButtons}>
              <NamedLink name="SearchPage" className={css.ppCtaPrimary}>
                <FormattedMessage id="PurchaseProtectionPage.ctaBrowse" />
              </NamedLink>
              <NamedLink name="NewListingPage" className={css.ppCtaSecondary}>
                <FormattedMessage id="PurchaseProtectionPage.ctaList" />
              </NamedLink>
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const { bool } = PropTypes;

PurchaseProtectionPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const PurchaseProtectionPage = compose(
  withRouter,
  connect(mapStateToProps, null),
  withViewport,
  injectIntl
)(PurchaseProtectionPageComponent);

export default PurchaseProtectionPage;
